import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import {
  TimerOutlined,
  HealthAndSafetyOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";
import { WorkOrdersState } from "../../models/models";
import { fetchAnalytics } from "../../store/workOrdersSlice";
import AnalyticsHeader, { TimeRange } from "./AnalyticsHeader";
import KpiCard from "./KpiCard";
import CompletionTrendChart from "./CompletionTrendChart";
import CostTrendChart from "./CostTrendChart";
import TypeDistributionChart from "./TypeDistributionChart";
import TopMachinesChart from "./TopMachinesChart";

const getDateRange = (range: TimeRange, customFrom: string, customTo: string) => {
  const now = new Date();
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // end of today
  let from: Date;

  switch (range) {
    case "7d":
      from = new Date(to);
      from.setDate(from.getDate() - 7);
      break;
    case "30d":
      from = new Date(to);
      from.setDate(from.getDate() - 30);
      break;
    case "90d":
      from = new Date(to);
      from.setDate(from.getDate() - 90);
      break;
    case "custom":
      from = customFrom ? new Date(customFrom) : new Date(to.getTime() - 30 * 86400000);
      return {
        dateFrom: from.toISOString(),
        dateTo: customTo ? new Date(customTo).toISOString() : to.toISOString(),
      };
    default:
      from = new Date(to);
      from.setDate(from.getDate() - 30);
  }

  return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
};

const AnalyticsDashboardPage: React.FC = () => {
  useRedesignSetup();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("operations");

  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1200px)");

  const { analytics, analyticsLoading } = useAppSelector<WorkOrdersState>(
    (state: any) => state.workOrders
  );

  const [timeRange, setTimeRange] = useState<TimeRange>("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const dateRange = useMemo(
    () => getDateRange(timeRange, customFrom, customTo),
    [timeRange, customFrom, customTo]
  );

  const loadData = useCallback(() => {
    dispatch(fetchAnalytics(dateRange));
  }, [dispatch, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useDataRefresh("workOrders", loadData);
  useVisibilityRefresh(loadData);

  const formatHours = (hours: number) => {
    if (hours === 0) return "N/A";
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours.toFixed(1)}h`;
  };

  const kpiGridColumns = isPhone ? "1fr" : isTablet ? "1fr 1fr" : "1fr 1fr 1fr 1fr";
  const chartGridColumns = isPhone ? "1fr" : "1fr 1fr";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: dt.pageBg,
        backgroundImage: `radial-gradient(${dt.pageDotColor} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
        p: isPhone ? 2 : 3,
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        <AnalyticsHeader
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />

        {analyticsLoading && !analytics && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
            }}
          >
            <CircularProgress size={32} sx={{ color: dt.blue }} />
          </Box>
        )}

        {analytics && (
          <>
            {/* KPI Cards Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: kpiGridColumns,
                gap: 2,
                mb: 3,
              }}
            >
              <KpiCard
                label={translate("analytics.mttr")}
                value={formatHours(analytics.meanTimeToRepairHours)}
                icon={<TimerOutlined sx={{ fontSize: 20 }} />}
                color="#3498DB"
                tooltip={translate("analytics.mttrTooltip")}
              />
              <KpiCard
                label={translate("analytics.mtbf")}
                value={formatHours(analytics.meanTimeBetweenFailuresHours)}
                icon={<HealthAndSafetyOutlined sx={{ fontSize: 20 }} />}
                color="#10B981"
                tooltip={translate("analytics.mtbfTooltip")}
              />
              <KpiCard
                label={translate("analytics.completionRate")}
                value={`${analytics.completionRate}%`}
                icon={<CheckCircleOutlined sx={{ fontSize: 20 }} />}
                color="#059669"
                tooltip={translate("analytics.completionRateTooltip")}
              />
              <KpiCard
                label={translate("analytics.overdue")}
                value={analytics.overdueCount.toString()}
                icon={<WarningAmberOutlined sx={{ fontSize: 20 }} />}
                color={analytics.overdueCount > 0 ? "#EF4444" : "#9CA3AF"}
                tooltip={translate("analytics.overdueTooltip")}
                trendPositive={false}
              />
            </Box>

            {/* Trend Charts Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: chartGridColumns,
                gap: 2,
                mb: 3,
              }}
            >
              <CompletionTrendChart
                completionTrend={analytics.completionTrend}
                createdTrend={analytics.createdTrend}
              />
              <CostTrendChart costTrend={analytics.costTrend} />
            </Box>

            {/* Breakdown Charts Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: chartGridColumns,
                gap: 2,
                mb: 3,
              }}
            >
              <TypeDistributionChart byType={analytics.byType} />
              <TopMachinesChart topMachines={analytics.topMachines} />
            </Box>

            {/* Top Assignees */}
            {analytics.topAssignees.length > 0 && (
              <Box
                sx={{
                  background: dt.cardBg,
                  borderRadius: dt.radius,
                  border: `1px solid ${dt.cardBorder}`,
                  boxShadow: dt.cardShadow,
                  p: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "15px",
                    fontWeight: 600,
                    color: dt.textPrimary,
                    mb: 2,
                  }}
                >
                  {translate("analytics.topAssignees")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isPhone
                      ? "1fr"
                      : isTablet
                      ? "1fr 1fr"
                      : "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: 1.5,
                  }}
                >
                  {analytics.topAssignees.map(a => (
                    <Box
                      key={a.userId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: dt.radiusSm,
                        background: dt.pageBg,
                        transition: "all 0.15s ease",
                        "&:hover": { background: dt.blueSoft },
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${dt.blue}, ${dt.blueHover})`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontFamily: dt.font,
                          fontSize: "14px",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {a.userName.charAt(0).toUpperCase()}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: dt.font,
                            fontSize: "13px",
                            fontWeight: 600,
                            color: dt.textPrimary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {a.userName}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: dt.fontMono,
                            fontSize: "12px",
                            color: dt.textMuted,
                          }}
                        >
                          {a.completedCount} {translate("analytics.totalCompleted").toLowerCase()}
                          {a.avgCompletionHours > 0 && ` / ${formatHours(a.avgCompletionHours)} avg`}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Empty state */}
            {analytics.totalCreated === 0 && analytics.totalCompleted === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "14px",
                    color: dt.textMuted,
                  }}
                >
                  {translate("analytics.noData")}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsDashboardPage;
